import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { BookingModalService } from '../../core/services/booking-modal.service';
import { FormService } from '../../core/services/form.service';
import { AnalyticsService } from '../../core/services/analytics.service';
import { Subscription } from 'rxjs';

interface StepConfig {
  id: number;
  title: string;
  subtitle: string;
  icon: string;
}

@Component({
  selector: 'app-booking-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './booking-modal.component.html',
  styleUrls: ['./booking-modal.component.scss']
})
export class BookingModalComponent implements OnInit, OnDestroy {
  isOpen = false;
  currentStep = 1;
  totalSteps = 2;
  bookingForm: FormGroup;
  isSubmitting = false;
  submitSuccess = false;
  submitMessage = '';
  preselectedServiceId: string | null = null;
  private subscription?: Subscription;

  steps: StepConfig[] = [
    { id: 1, title: 'Service Type', subtitle: 'What are you looking for?', icon: 'bi-grid-3x3-gap' },
    { id: 2, title: 'Your Details', subtitle: 'Tell us about yourself', icon: 'bi-person' }
  ];

  services = [
    { id: 'web-design', name: 'Web Design & Development', icon: 'bi-laptop' },
    { id: 'app-development', name: 'App Development', icon: 'bi-phone' },
    { id: 'ui-ux', name: 'UI/UX Design', icon: 'bi-palette' },
    { id: 'digital-marketing', name: 'Digital Marketing', icon: 'bi-megaphone' },
    { id: 'branding', name: 'Brand Identity', icon: 'bi-brush' },
    { id: 'seo', name: 'SEO & Marketing', icon: 'bi-graph-up-arrow' },
    { id: 'consultation', name: 'General Consultation', icon: 'bi-chat-dots' },
    { id: 'other', name: 'Other Services', icon: 'bi-three-dots' }
  ];

  constructor(
    private fb: FormBuilder,
    private bookingModalService: BookingModalService,
    private formService: FormService,
    private analytics: AnalyticsService
  ) {
    this.bookingForm = this.fb.group({
      // Step 1: Service
      service: ['', Validators.required],

      // Step 2: Personal Details
      fullName: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.required, Validators.pattern(/^[\+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,5}[-\s\.]?[0-9]{1,6}$/)]],
      message: ['']
    });
  }

  ngOnInit() {
    this.subscription = this.bookingModalService.modal$.subscribe(payload => {
      this.isOpen = payload.isOpen;

      if (payload.isOpen) {
        if (payload.serviceId) {
          // Pre-fill service and jump to step 2
          const normalizedServiceId = this.normalizeServiceId(payload.serviceId);
          this.preselectedServiceId = normalizedServiceId;
          this.bookingForm.patchValue({ service: normalizedServiceId });
          this.currentStep = 2;
        } else {
          this.preselectedServiceId = null;
          this.currentStep = 1;
        }
      }
    });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  closeModal() {
    this.bookingModalService.close();
    this.resetForm();
  }

  nextStep() {
    if (this.validateCurrentStep()) {
      if (this.currentStep < this.totalSteps) {
        this.currentStep++;
      }
    } else {
      this.markStepAsTouched();
    }
  }

  previousStep() {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  goToStep(step: number) {
    if (step <= this.currentStep) {
      this.currentStep = step;
    }
  }

  /** Navigate back to service selection (step 1) when pre-selected */
  changeService() {
    this.preselectedServiceId = null;
    this.currentStep = 1;
  }

  validateCurrentStep(): boolean {
    switch (this.currentStep) {
      case 1:
        return this.bookingForm.get('service')?.valid || false;
      case 2:
        return (
          this.bookingForm.get('fullName')?.valid &&
          this.bookingForm.get('email')?.valid &&
          this.bookingForm.get('phone')?.valid
        ) || false;
      default:
        return false;
    }
  }

  markStepAsTouched() {
    switch (this.currentStep) {
      case 1:
        this.bookingForm.get('service')?.markAsTouched();
        break;
      case 2:
        this.bookingForm.get('fullName')?.markAsTouched();
        this.bookingForm.get('email')?.markAsTouched();
        this.bookingForm.get('phone')?.markAsTouched();
        break;
    }
  }

  selectService(serviceId: string) {
    this.bookingForm.patchValue({ service: this.normalizeServiceId(serviceId) });
    // Auto-advance to step 2 after a brief visual feedback
    setTimeout(() => this.currentStep = 2, 250);
  }

  private normalizeServiceId(serviceId: string): string {
    const bookingServiceIds = new Set(this.services.map(service => service.id));
    if (bookingServiceIds.has(serviceId)) {
      return serviceId;
    }

    const normalized = serviceId.toLowerCase();

    if (
      normalized.includes('app') ||
      normalized.includes('pwa') ||
      normalized.includes('web-application')
    ) {
      return 'app-development';
    }

    if (
      normalized.includes('marketing') ||
      normalized.includes('ads') ||
      normalized.includes('google') ||
      normalized.includes('facebook') ||
      normalized.includes('instagram') ||
      normalized.includes('linkedin') ||
      normalized.includes('twitter') ||
      normalized.includes('youtube') ||
      normalized.includes('tiktok')
    ) {
      return 'digital-marketing';
    }

    if (
      normalized.includes('web-design') ||
      normalized.includes('wordpress') ||
      normalized.includes('website') ||
      normalized.includes('landing-page') ||
      normalized.includes('ecommerce')
    ) {
      return 'web-design';
    }

    if (normalized.includes('brand')) {
      return 'branding';
    }

    if (normalized.includes('ui') || normalized.includes('ux')) {
      return 'ui-ux';
    }

    if (normalized.includes('seo')) {
      return 'seo';
    }

    if (normalized.includes('consult')) {
      return 'consultation';
    }

    return 'other';
  }

  /** Get display name for the selected service */
  getSelectedServiceName(): string {
    const serviceId = this.bookingForm.get('service')?.value;
    const found = this.services.find(s => s.id === serviceId);
    return found?.name || serviceId;
  }

  onSubmit() {
    if (this.bookingForm.valid) {
      this.isSubmitting = true;
      this.submitMessage = '';

      this.formService.submitBookingForm(this.bookingForm.value).subscribe({
        next: (response) => {
          this.isSubmitting = false;
          this.submitSuccess = true;
          this.submitMessage = response.message || 'Thank you! We\'ll contact you within 2 hours to discuss your project.';
          this.analytics.trackLead('booking_form_submit', { service: this.bookingForm.value.service });

          setTimeout(() => {
            this.closeModal();
          }, 3000);
        },
        error: (error) => {
          this.isSubmitting = false;
          this.submitSuccess = false;
          this.submitMessage = error.message || 'An error occurred. Please try again later.';

          setTimeout(() => {
            this.submitMessage = '';
          }, 5000);
        }
      });
    } else {
      this.markStepAsTouched();
    }
  }

  resetForm() {
    this.currentStep = 1;
    this.preselectedServiceId = null;
    this.bookingForm.reset();
    this.submitSuccess = false;
    this.submitMessage = '';
  }

  getProgressPercentage(): number {
    return (this.currentStep / this.totalSteps) * 100;
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.bookingForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  getErrorMessage(fieldName: string): string {
    const field = this.bookingForm.get(fieldName);
    if (field?.hasError('required')) {
      return 'This field is required';
    }
    if (field?.hasError('email')) {
      return 'Please enter a valid email';
    }
    if (field?.hasError('minlength')) {
      return `Minimum ${field.errors?.['minlength'].requiredLength} characters required`;
    }
    if (field?.hasError('pattern')) {
      if (fieldName === 'phone') {
        return 'Please enter a valid phone number (international format supported)';
      }
      return 'Invalid format';
    }
    return '';
  }
}
